import { useApi } from '@hero/context';
import { useQueryAPI } from '../../utils';
import type { RegionSearchAPI } from '@hero/api';
import type { HookQueryApiConfig } from '../../types/hook-api';
import type { UseQueryResult } from '@tanstack/react-query';

export type RegionSearchHookAPI = HookQueryApiConfig<RegionSearchAPI>;

/**
 * Free-text region search across English / Arabic / Urdu / transliteration /
 * customCode. Powers the dispatcher picker AND the NLU/LLM consumers that
 * parse free text like "2 trucks from Riyadh to جدة".
 *
 * Backed by `GET /region/search` (Atlas `$search` + synonyms). Public —
 * no auth required.
 *
 * Disabled when `q` is empty / shorter than 2 characters to spare the network
 * (Atlas Search is precise enough that ≥2 chars is a useful filter).
 */
export type UseRegionSearchApi = (
  query: RegionSearchAPI['req'],
  apiConfig?: RegionSearchHookAPI,
) => UseQueryResult<RegionSearchAPI['data'], Error>;

export const useRegionSearchApi: UseRegionSearchApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'region',
    apiFn: api.common.region.search,
    query,
    apiConfig,
    isEnabled: () => Boolean(query?.q && query.q.trim().length >= 2),
  });
};
